import {
  Address,
  ValidationRequest,
  AddressValidationResponse,
  PossibleNextAction
} from "@/models/ApplicationForm";

import {
  assignIncrementingIds,
  BlacklistedTerm, DataSet,
  englishDataset,
  englishRecommendedTransformers, ParsedPattern, pattern,
  RegExpMatcher
} from "obscenity";


export class AddressValidationState {
  requireValidAddress: boolean;
  isAddressValid: boolean;
  showSuggestedAddress: boolean;
  errorMessage: string;
  previousCheckedAddress: string;
  subPremiseMessage: string;
  validationRequest: Promise<AddressValidationResponse> | null;
  response: AddressValidationResponse | null;

  constructor(requireValidAddress: boolean, subPremiseMessage?: string) {
    this.requireValidAddress = requireValidAddress;
    this.isAddressValid = false;
    this.showSuggestedAddress = false;
    this.errorMessage = "";
    this.previousCheckedAddress = "";
    this.validationRequest = null;
    this.subPremiseMessage = subPremiseMessage ?? "Please add a sub-premise, this might be an apartment or unit number.";
    this.response = null;
  }

  /**
   * @name reset
   * @desc Resets the validation state for the address
   * @input isEditing - If true will set the address to valid initially unless it is modified
   */
  reset(isEditing?: boolean): void {
    this.errorMessage = "";
    this.isAddressValid = isEditing == true;
    this.previousCheckedAddress = "";
    this.showSuggestedAddress = false;
    this.validationRequest = null;
    this.response = null;
  }

  /**
   * @name setError
   * @desc Sets the address to invalid with the provided error message
   * @input message - The message to display on why the address is invalid
   */
  setError(message: string): void {
    this.isAddressValid = false;
    this.errorMessage = message;
  }

  /**
   * @name setValid
   * @desc Sets the address to valid
   */
  setValid(): void {
    this.showSuggestedAddress = false;
    this.isAddressValid = true;
    this.errorMessage = "";
  }

  /**
   * @name processRequest
   * @desc Processes the provided validation request by calling Google's API then handles the associated response
   * @input validationRequest - An AddressValidationResponse promise to parse
   * @input previousCheckedAddress - The most recent previously checked address
   */
  processRequest(validationRequest: Promise<AddressValidationResponse>, previousCheckedAddress: string): void {
    this.validationRequest = validationRequest;
    this.previousCheckedAddress = previousCheckedAddress;
    this.showSuggestedAddress = false;
    this.errorMessage = "";
    this.isAddressValid = false;

    this.validationRequest.then((response: AddressValidationResponse) => {
      if (response == null) {
        console.error("Unable to validate address, marking it as validated");
        this.setValid();
        return;
      }

      this.response = response;

      if (this.requireValidAddress) {
        if (this.response?.verdict.possibleNextAction === PossibleNextAction.FIX) {
          this.setError("The provided address is invalid. Please fix any potential issues.");
          return;
        }

        if (this.response?.verdict.possibleNextAction === PossibleNextAction.CONFIRM_ADD_SUBPREMISES) {
          this.setError(this.subPremiseMessage);
          return;
        }

        if (
          this.response?.verdict.possibleNextAction === PossibleNextAction.CONFIRM
          || this.response?.verdict.hasReplacedComponents === true
        ) {
          this.setError("Please confirm that all parts of the address are provided and correct.");
          return;
        }
      }

      if (this.isRecommendedAddressSame() || this.response?.googleCorrectedAddress == null) {
        this.setValid();
      }
      else {
        this.errorMessage = "";
        this.showSuggestedAddress = true;
      }
    });
  }

  /**
   * @name hasRequestFinished
   * @desc Waits for any current address validation requests to finish and process before returning. Useful
   * when attempting to save.
   * @returns Promise<void>
   */
  async hasRequestFinished(): Promise<void> {
    // Waits for any active address validation calls to return first
    if (this.validationRequest) {
      await this.validationRequest;
      this.validationRequest = null;
    }
    return;
  }

  /**
   * @name getErrorMessage
   * @desc Gets the error message
   * @returns string - The error message if present or an empty string if no error
   */
  getErrorMessage(): string {
    if (this.isAddressValid !== false) {
      return "";
    }
    return this.errorMessage;
  }

  /**
   * @name getErrorMessage
   * @desc Gets the recommended address by Google
   * @returns string - The recommended address by Google in Address formatting
   */
  getSuggestedAddress(): string {
    const addr = this.response?.googleCorrectedAddress;
    if (addr == null) {
      return "";
    }
    return this.addressToStr(addr);
  }

  /**
   * @name addressToStr
   * @input address - The address object to convert into an address str
   * @desc Converts an address object to a formatted address string including commas
   * @returns string
   */
  addressToStr(address: Address): string {
    return address.street + ", " + address.city + ", " + address.state + " " + address.zipcode;
  }

  /**
   * @name getErrorMessage
   * @desc Determines if the Google recommended address is the same as the user provided address
   * @returns boolean
   */
  isRecommendedAddressSame(): boolean {
    if (
      this.response?.googleCorrectedAddress == null
      || this.previousCheckedAddress == ""
      ) {
      return false;
    }

    return this.previousCheckedAddress === this.getSuggestedAddress();
  }
}


export class ValidationRules {
  private customDataset = new DataSet()
    .addAll(englishDataset)
    .addPhrase((phrase) =>
      phrase
        .addPattern(pattern`|damn|`)
        .addPattern(pattern`bltch`)
        .addPattern(pattern`|hell|`)
    );

  profanityMatcher = new RegExpMatcher({
    ...this.customDataset.build(),
    ...englishRecommendedTransformers,
  });

  /**
   * @name genericFieldRule
   * @input maxLength - The maximum amount of characters aloud
   * @input ignoreProfanity - If true then a rule checking for profanity will not be added
   * @input additional Rules - Any additional rules that should be used for the field
   * @desc Performs base validation for a text input field, factoring in
       character size limit, profanity, and additional rules.
   */
  genericFieldRule(maxLength: number, ignoreProfanity?: boolean | null, additionalRules: (() => boolean | string)[] = []) {
    let rules = [
      (v: any) => !!v || 'Field is Required',
      (v: any) => (v && v.length <= maxLength) || 'Field must be less than ' + (maxLength + 1) + ' characters'
    ];

    if (ignoreProfanity !== true) {
     rules.push((v: any) => v && !this.profanityMatcher.hasMatch(v) || 'Avoid inappropriate language');
    }

    if (additionalRules.length > 0) {
      // Appends all the additional rules to the genericFieldRules
      additionalRules.forEach(rule => {rules.push(rule)});
    }
    return rules;
  }

}

